import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Search, MoreVertical, ShieldAlert, ShieldCheck, FileText, Pencil, Trash2 } from 'lucide-react';
import api from '../../services/api';
import { Toast } from '../../Toast';

export const UsersList: React.FC = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [toast, setToast] = useState<{ msg: string, type: 'success' | 'error' } | null>(null);
    const [actionsOpenId, setActionsOpenId] = useState<string | null>(null);
    const [confirmDelete, setConfirmDelete] = useState<{ user: any } | null>(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            setLoading(true);
            const res = await api.get('/banana/users');
            setUsers(res.data.users);
        } catch (error) {
            console.error('Erro ao carregar usuários:', error);
        } finally {
            setLoading(false);
        }
    };

    const toggleStatus = async (id: string, currentStatus: boolean) => {
        try {
            await api.patch(`/banana/users/${id}/status`, { is_active: !currentStatus });
            setUsers(users.map(u => u.id === id ? { ...u, is_active: !currentStatus } : u));
            setToast({ msg: `Usuário ${!currentStatus ? 'ativado' : 'bloqueado'} com sucesso!`, type: 'success' });
        } catch (error) {
            setToast({ msg: 'Erro ao alterar status do usuário.', type: 'error' });
        }
    };

    const handleDeleteUser = async (user: any) => {
        setDeleting(true);
        try {
            await api.delete(`/banana/users/${user.id}`);
            setUsers(prev => prev.filter(u => u.id !== user.id));
            setConfirmDelete(null);
            setActionsOpenId(null);
            setToast({ msg: 'Usuário excluído.', type: 'success' });
        } catch (e: any) {
            setToast({ msg: e.response?.data?.error || 'Não foi possível excluir o usuário.', type: 'error' });
        } finally {
            setDeleting(false);
        }
    };

    const filteredUsers = users.filter(u =>
        (u.phone && u.phone.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (u.email && u.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (u.full_name && u.full_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="p-6">
            {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

            <div className="flex justify-between items-center mb-8">
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                    <Users className="text-orange-500" /> Gestão de Clientes
                </h1>

                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                    <input
                        type="text"
                        placeholder="Buscar por nome, telefone ou email..."
                        className="bg-white/5 border border-white/10 rounded-xl py-2 pl-10 pr-4 text-white text-sm w-80 focus:border-orange-500/50 outline-none transition-all"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
                <table className="w-full text-left">
                    <thead className="text-xs text-gray-500 border-b border-white/10 bg-white/5">
                        <tr>
                            <th className="p-5 font-normal uppercase tracking-wider">Cliente / Identificação</th>
                            <th className="p-5 font-normal uppercase tracking-wider">Status</th>
                            <th className="p-5 font-normal uppercase tracking-wider">Bots</th>
                            <th className="p-5 font-normal uppercase tracking-wider">Cadastro</th>
                            <th className="p-5 font-normal uppercase tracking-wider text-right">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm">
                        {loading ? (
                            <tr><td colSpan={5} className="p-20 text-center text-gray-500">Carregando usuários...</td></tr>
                        ) : filteredUsers.length === 0 ? (
                            <tr><td colSpan={5} className="p-20 text-center text-gray-500">Nenhum usuário encontrado.</td></tr>
                        ) : filteredUsers.map(user => (
                            <tr key={user.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors">
                                <td className="p-5">
                                    <div className="flex flex-col">
                                        <span className="text-white font-medium">{user.full_name || 'Sem Nome'}</span>
                                        <div className="flex gap-2 items-center">
                                            <small className="text-gray-500">{user.email || 'Sem email'}</small>
                                            <span className="text-gray-700">•</span>
                                            <small className="text-orange-500/70">{user.phone}</small>
                                        </div>
                                    </div>
                                </td>
                                <td className="p-5">
                                    <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${user.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                        {user.is_active ? 'ATIVO' : 'BLOQUEADO'}
                                    </span>
                                </td>
                                <td className="p-5 text-gray-400">
                                    {user.bots?.length || 0} bots operando
                                </td>
                                <td className="p-5 text-gray-500">
                                    {new Date(user.created_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                                </td>
                                <td className="p-5 text-right">
                                    <div className="flex justify-end gap-2 items-center">
                                        <button
                                            onClick={() => navigate(`/banana/usuarios/${user.id}`, { state: { user } })}
                                            className="text-orange-500 hover:text-orange-400 font-bold text-xs uppercase transition-colors flex items-center gap-1"
                                            title="Ver detalhes"
                                        >
                                            <FileText size={16} /> Detalhes
                                        </button>
                                        <button
                                            onClick={() => toggleStatus(user.id, user.is_active)}
                                            className={`p-2 rounded-lg transition-colors ${user.is_active ? 'hover:bg-red-500/10 text-gray-500 hover:text-red-400' : 'hover:bg-green-500/10 text-gray-500 hover:text-green-400'}`}
                                            title={user.is_active ? 'Bloquear Usuário' : 'Ativar Usuário'}
                                        >
                                            {user.is_active ? <ShieldAlert size={18} /> : <ShieldCheck size={18} />}
                                        </button>
                                        <div className="relative">
                                            <button
                                                type="button"
                                                onClick={() => setActionsOpenId(actionsOpenId === user.id ? null : user.id)}
                                                className="p-2 rounded-lg text-gray-500 hover:text-white hover:bg-white/5 transition-colors"
                                                aria-label="Ações"
                                            >
                                                <MoreVertical size={18} />
                                            </button>
                                            {actionsOpenId === user.id && (
                                                <>
                                                    <div className="fixed inset-0 z-10" aria-hidden onClick={() => setActionsOpenId(null)} />
                                                    <div className="absolute right-0 top-full mt-1 z-20 min-w-[180px] py-1 bg-white/5 border border-white/10 rounded-xl shadow-xl backdrop-blur-sm">
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setActionsOpenId(null);
                                                                navigate(`/banana/usuarios/${user.id}`, { state: { user, edit: true } });
                                                            }}
                                                            className="w-full px-4 py-2.5 text-left text-sm text-orange-500 hover:bg-white/10 hover:text-orange-400 flex items-center gap-2 transition-colors"
                                                        >
                                                            <Pencil size={14} /> Editar usuário
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setActionsOpenId(null);
                                                                setConfirmDelete({ user });
                                                            }}
                                                            className="w-full px-4 py-2.5 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2 transition-colors"
                                                        >
                                                            <Trash2 size={14} /> Excluir usuário
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {confirmDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => !deleting && setConfirmDelete(null)}>
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 max-w-md w-full shadow-xl" onClick={e => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-white mb-2">Excluir usuário?</h3>
                        <p className="text-gray-400 text-sm mb-6">
                            O usuário <strong className="text-white">{confirmDelete.user.full_name || confirmDelete.user.email}</strong> será removido da plataforma. Contas Telegram e bots serão desvinculados. Esta ação não pode ser desfeita.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button
                                type="button"
                                onClick={() => setConfirmDelete(null)}
                                disabled={deleting}
                                className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm font-medium transition-colors disabled:opacity-50"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={() => handleDeleteUser(confirmDelete.user)}
                                disabled={deleting}
                                className="px-4 py-2 rounded-xl bg-red-500/20 hover:bg-red-500/30 text-red-400 text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                            >
                                {deleting ? 'Excluindo...' : <><Trash2 size={14} /> Excluir</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
